import * as React from 'react';
import { useState } from 'react';
import { Check, Pencil } from 'lucide-react';

import { Input, useSideDrawerEscapeInterceptor } from '@klareospace/pharos-lib-web-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ServiceTimeFieldProps {
  /** Accessible label for the confirm (check) button shown while editing. */
  confirmAriaLabel: string;
  /** Already-interpolated "{value} {unit}" display string for the read-only view (built by the caller via i18n). */
  displayValue: string;
  /** Accessible label for the pencil button that enters edit mode. */
  editAriaLabel: string;
  /** Accessible label for the number input shown while editing. */
  inputAriaLabel: string;
  /** Label shown before the value (e.g. "Pickup service time"). */
  label: string;
  onChange: (value: number) => void;
  /** Unit suffix shown after the value while editing (e.g. "min"). */
  unitLabel: string;
  value: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STEP_MINUTES = 5;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Read-only "{value} {unit}" by default; a pencil reveals a number input (step 5, min 0) with a
 * check to confirm. The draft is kept as a plain string while editing so an in-progress edit (e.g.
 * clearing the field to retype) never flashes to "0" — only `handleConfirm` parses and clamps it.
 * Enter confirms, Escape discards the draft and leaves edit mode — without also triggering a
 * dismiss on any ancestor `SideDrawer` (see `useSideDrawerEscapeInterceptor`). Mirrors the UX
 * reference's `ServiceTimeField` — used for both the pickup and delivery service time, each
 * instance owning its own edit state.
 */
export function ServiceTimeField({
  confirmAriaLabel,
  displayValue,
  editAriaLabel,
  inputAriaLabel,
  label,
  onChange,
  unitLabel,
  value,
}: ServiceTimeFieldProps): React.JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));

  function handleStartEditing(): void {
    setDraft(String(value));
    setIsEditing(true);
  }

  function handleConfirm(): void {
    onChange(parseDraft(draft));
    setIsEditing(false);
  }

  function handleCancel(): void {
    setDraft(String(value));
    setIsEditing(false);
  }

  function handleEscapeIntercept(): boolean {
    handleCancel();
    return true;
  }

  // Claims Escape from the nearest ancestor SideDrawer while editing, so cancelling the draft
  // never also opens the drawer's close-confirm dialog. A no-op when rendered standalone (outside
  // any SideDrawer) — the `onKeyDown` handling below is what cancels the edit in that case.
  useSideDrawerEscapeInterceptor(isEditing, handleEscapeIntercept);

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') handleConfirm();
    if (event.key === 'Escape') {
      // Belt-and-suspenders for the standalone case (no SideDrawer ancestor): stopPropagation only
      // affects bubble-phase listeners on this subtree's ancestors, not Radix's capture-phase one.
      event.stopPropagation();
      handleCancel();
    }
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Input
          aria-label={inputAriaLabel}
          className="h-8 w-20 text-sm"
          min={0}
          onChange={(event): void => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          step={STEP_MINUTES}
          type="number"
          value={draft}
        />
        <span className="text-xs text-muted-foreground">{unitLabel}</span>
        <button
          aria-label={confirmAriaLabel}
          className="cursor-pointer rounded-md p-1 text-primary hover:bg-muted hover:text-primary/80"
          onClick={handleConfirm}
          type="button"
        >
          <Check aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="truncate text-sm font-medium text-foreground">{displayValue}</span>
      <button
        aria-label={editAriaLabel}
        className="shrink-0 cursor-pointer rounded-md p-1 text-primary hover:bg-muted hover:text-primary/80"
        onClick={handleStartEditing}
        type="button"
      >
        <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Parses the draft string on confirm; an empty or non-numeric draft falls back to 0, clamped to a non-negative integer. */
function parseDraft(draft: string): number {
  const parsed = Number(draft);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
}
